import { useMemo } from "react";
import FindingCard from "@/components/FindingCard";
import CopyButton from "@/components/CopyButton";
import { downloadMarkdown } from "@/lib/exportReview";
import { USE_CASE_MAP } from "@/data/useCases";
import { Download, RotateCw, UserPlus } from "lucide-react";

export default function ResultPack({ review, onReset, onSaveAccount, savedToAccount }) {
  const uc = USE_CASE_MAP[review.document_type] || { label: review.document_type };
  const counts = review.risk_counts || {};

  const sortedFindings = useMemo(() => {
    const order = { red: 0, amber: 1, green: 2 };
    return [...(review.findings || [])].sort(
      (a, b) => (order[a.severity] ?? 3) - (order[b.severity] ?? 3)
    );
  }, [review.findings]);

  const issueListText = useMemo(() => {
    return sortedFindings
      .map((f, i) => {
        const line = `${i + 1}. [${(f.severity || "").toUpperCase()}] ${f.title}${
          f.page_number ? ` (p.${f.page_number})` : ""
        }`;
        return `${line}\n   Why: ${f.why_it_matters}\n   Action: ${f.recommended_action}`;
      })
      .join("\n\n");
  }, [sortedFindings]);

  return (
    <section
      data-testid="result-pack"
      className="max-w-[1280px] mx-auto px-6 md:px-10 py-12"
    >
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
        <div>
          <div className="eyebrow mb-3">Review pack · {uc.label}</div>
          <h1
            className="font-serif-display text-3xl md:text-4xl text-ink leading-tight"
            data-testid="result-filename"
          >
            {review.filename}
          </h1>
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            data-testid="result-download-md"
            className="btn-outline focus-ring"
            onClick={() => downloadMarkdown(review)}
          >
            <Download className="w-4 h-4" /> Download Markdown
          </button>
          <button
            data-testid="result-restart-btn"
            className="btn-ghost focus-ring"
            onClick={onReset}
          >
            <RotateCw className="w-4 h-4" /> Review another
          </button>
        </div>
      </div>

      {!savedToAccount && (
        <div
          data-testid="result-save-cta"
          className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4 border border-ink-20 bg-white p-5"
        >
          <div>
            <div className="eyebrow mb-1">Guest review</div>
            <div className="text-sm text-ink-70">
              This review isn't saved. Create an account to keep it in your history.
            </div>
          </div>
          <button
            onClick={onSaveAccount}
            className="btn-primary focus-ring"
            data-testid="result-save-account-btn"
          >
            <UserPlus className="w-4 h-4" /> Create account
          </button>
        </div>
      )}

      {/* SUMMARY */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-10">
        <div className="md:col-span-8 paper-card">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="eyebrow">Executive summary</div>
            <CopyButton
              text={review.summary || ""}
              label="Copy summary"
              testId="copy-summary-btn"
            />
          </div>
          <p
            className="text-base text-ink leading-relaxed whitespace-pre-wrap"
            data-testid="result-summary"
          >
            {review.summary || "No summary produced."}
          </p>
        </div>

        <div className="md:col-span-4 paper-card">
          <div className="eyebrow mb-4">Risk counts</div>
          <div className="space-y-3">
            {[
              { k: "red", l: "Red — material" },
              { k: "amber", l: "Amber — review" },
              { k: "green", l: "Green — favorable" },
            ].map((r) => (
              <div key={r.k} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className={`pill pill-${r.k}`}>
                    <span className="dot" />
                    {r.k}
                  </span>
                  <span className="text-sm text-ink-70">{r.l}</span>
                </div>
                <div
                  className="font-serif-display text-2xl text-ink"
                  data-testid={`result-count-${r.k}`}
                >
                  {counts[r.k] || 0}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* MISSING CLAUSES */}
      {review.missing_clauses?.length > 0 && (
        <div className="paper-card mb-10">
          <div className="eyebrow mb-4">Missing clauses</div>
          <div className="flex flex-wrap gap-2">
            {review.missing_clauses.map((m, i) => (
              <span
                key={i}
                data-testid={`missing-clause-${i}`}
                className="pill pill-amber"
              >
                <span className="dot" />
                {m}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* FINDINGS */}
      <div className="mb-10">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="eyebrow mb-2">Findings</div>
            <h2 className="font-serif-display text-2xl text-ink">
              {sortedFindings.length} issue{sortedFindings.length === 1 ? "" : "s"}, prioritized
            </h2>
          </div>
          <CopyButton
            text={issueListText}
            label="Copy issue list"
            testId="copy-issues-btn"
          />
        </div>

        {sortedFindings.length === 0 ? (
          <div className="paper-card text-ink-50">No findings for this document.</div>
        ) : (
          <div className="space-y-4">
            {sortedFindings.map((f, i) => (
              <FindingCard key={i} f={f} index={i} />
            ))}
          </div>
        )}
      </div>

      {/* MEMO */}
      {review.memo && (
        <div className="paper-card mb-10">
          <div className="flex items-start justify-between mb-4">
            <div className="eyebrow">Client memo</div>
            <CopyButton text={review.memo} label="Copy memo" testId="copy-memo-btn" />
          </div>
          <p
            className="text-sm text-ink leading-relaxed whitespace-pre-wrap"
            data-testid="result-memo"
          >
            {review.memo}
          </p>
        </div>
      )}

      <p className="text-xs text-ink-50 font-mono-plex uppercase tracking-widest text-center mt-12">
        This is document review assistance — not legal, tax, or professional advice.
      </p>
    </section>
  );
}
